import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, RotateCcw, ListMusic, FileText, Clock, Headphones, AudioLines } from 'lucide-react';
import { API_BASE } from '@/lib/api';

function parseTimeToSeconds(timeStr) {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatTime(secs) {
  if (!secs || !isFinite(secs) || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function AudioPlayer({ product }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [audioSource, setAudioSource] = useState('full');
  const [activeChapter, setActiveChapter] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState(false);

  const hasFullAudio = !!product?.audio_url;
  const hasTeaserAudio = !!product?.teaser_audio_url;
  const hasChapterAudio = (product?.chaptered_script || []).some(ch => ch.audio_base64 || ch.audio_asset_id);
  const hasAnyAudio = hasFullAudio || hasTeaserAudio || hasChapterAudio;

  const chapters = React.useMemo(() => {
    const scripts = product?.chaptered_script || [];
    const timestamps = product?.chapter_timestamps || [];
    if (scripts.length > 0) {
      const estChapterDuration = (product?.duration_minutes || 15) * 60 / scripts.length;
      return scripts.map((ch, i) => ({
        index: i,
        number: ch.chapter || i + 1,
        title: ch.title || `Chapter ${i + 1}`,
        startTime: timestamps[i] ? parseTimeToSeconds(timestamps[i].start_time) : Math.round(i * estChapterDuration),
        content: ch.content || '',
        audioBase64: ch.audio_base64 || '',
        audioAssetId: ch.audio_asset_id || '',
      }));
    }
    if (timestamps.length > 0) {
      return timestamps.map((ts, i) => ({
        index: i,
        number: ts.chapter || i + 1,
        title: ts.title || `Chapter ${i + 1}`,
        startTime: parseTimeToSeconds(ts.start_time),
        content: '',
        audioBase64: '',
      }));
    }
    const outline = product?.outline || '';
    if (outline) {
      const lines = outline.split('\n').filter(l => l.trim());
      const estChapterDuration = (product?.duration_minutes || 15) * 60 / Math.max(lines.length, 1);
      return lines.map((line, i) => ({
        index: i,
        number: i + 1,
        title: line.replace(/^(Chapter\s*\d+[:\-.]?\s*)/i, '').trim(),
        startTime: Math.round(i * estChapterDuration),
        content: '',
        audioBase64: '',
      }));
    }
    return [];
  }, [product]);

  // Determine playback mode: per-chapter audio vs single audio file
  const useChapterAudio = chapters.some(ch => ch.audioBase64 || ch.audioAssetId);
  const [chapterAudioIndex, setChapterAudioIndex] = useState(0);

  const currentAudioUrl = React.useMemo(() => {
    if (useChapterAudio) {
      const ch = chapters[chapterAudioIndex];
      if (ch?.audioAssetId) return `${API_BASE}/assets/${ch.audioAssetId}/audio`;
      return ch?.audioBase64 ? `data:audio/mp3;base64,${ch.audioBase64}` : null;
    }
    return audioSource === 'full' ? product?.audio_url : product?.teaser_audio_url;
  }, [useChapterAudio, chapterAudioIndex, chapters, audioSource, product]);

  useEffect(() => {
    setAudioLoaded(false);
    setAudioError(false);
    setPlaying(false);
    setCurrentTime(0);
  }, [audioSource]);

  const updateActiveChapter = useCallback((time) => {
    if (!chapters.length) return;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (time >= chapters[i].startTime) {
        setActiveChapter(i);
        return;
      }
    }
    setActiveChapter(0);
  }, [chapters]);

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    setCurrentTime(t);
    updateActiveChapter(t);
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  const seekToChapter = (chapterIndex) => {
    if (!chapters[chapterIndex]) return;
    if (useChapterAudio) {
      setChapterAudioIndex(chapterIndex);
      setActiveChapter(chapterIndex);
      setCurrentTime(0);
      // Audio src will change via useEffect, auto-play after load
      setTimeout(() => {
        if (audioRef.current) { audioRef.current.play(); setPlaying(true); }
      }, 200);
    } else {
      if (!audioRef.current) return;
      audioRef.current.currentTime = chapters[chapterIndex].startTime;
      setActiveChapter(chapterIndex);
      if (!playing) { audioRef.current.play(); setPlaying(true); }
    }
  };

  // Auto-advance to next chapter in per-chapter mode
  const handleEnded = () => {
    setPlaying(false);
    if (useChapterAudio && chapterAudioIndex < chapters.length - 1) {
      const nextIdx = chapterAudioIndex + 1;
      if (chapters[nextIdx]?.audioBase64 || chapters[nextIdx]?.audioAssetId) {
        setChapterAudioIndex(nextIdx);
        setActiveChapter(nextIdx);
        setCurrentTime(0);
        setTimeout(() => {
          if (audioRef.current) { audioRef.current.play(); setPlaying(true); }
        }, 200);
      }
    }
  };

  const skipChapter = (dir) => {
    const next = (useChapterAudio ? chapterAudioIndex : activeChapter) + dir;
    if (next >= 0 && next < chapters.length) seekToChapter(next);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying(!playing);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    if (v === 0) setMuted(true);
    else setMuted(false);
  };

  const restart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    setActiveChapter(0);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const chapterProgress = React.useMemo(() => {
    if (!chapters.length || !duration) return 0;
    const ch = chapters[activeChapter];
    const nextCh = chapters[activeChapter + 1];
    const chStart = ch?.startTime || 0;
    const chEnd = nextCh?.startTime || duration;
    const chDuration = chEnd - chStart;
    if (chDuration <= 0) return 0;
    return Math.min(100, ((currentTime - chStart) / chDuration) * 100);
  }, [currentTime, duration, activeChapter, chapters]);

  if (!hasAnyAudio) {
    return (
      <Card data-testid="audio-player-no-audio">
        <CardContent className="p-8 text-center">
          <Headphones className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-base font-medium mb-1">Audio Coming Soon</p>
          <p className="text-sm text-muted-foreground">
            The audio for this guide is being prepared. In the meantime, you can read the transcript below.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={currentAudioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          const d = audioRef.current?.duration;
          if (d && isFinite(d)) { setDuration(d); }
          setAudioLoaded(true);
        }}
        onDurationChange={() => {
          const d = audioRef.current?.duration;
          if (d && isFinite(d)) { setDuration(d); }
        }}
        onEnded={handleEnded}
        onError={() => setAudioError(true)}
        preload="auto"
      />

      <Card className="overflow-hidden border-2 border-[#1A9DA5]/20">
        <div className="sparky-gradient px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Headphones className="w-6 h-6 text-white" />
            </div>
            <div className="text-white">
              <h3 className="font-semibold text-base font-['Outfit'] leading-tight">{product?.title}</h3>
              {chapters[activeChapter] && (
                <p className="text-white/80 text-xs mt-0.5">
                  Ch. {chapters[activeChapter].number}: {chapters[activeChapter].title}
                </p>
              )}
            </div>
          </div>
          {hasFullAudio && hasTeaserAudio && (
            <div className="flex gap-1">
              <Button
                size="sm" variant="ghost"
                className={`text-xs text-white/80 hover:text-white hover:bg-white/10 ${audioSource === 'full' ? 'bg-white/20 text-white' : ''}`}
                onClick={() => setAudioSource('full')}
                data-testid="audio-source-full"
              >Full</Button>
              <Button
                size="sm" variant="ghost"
                className={`text-xs text-white/80 hover:text-white hover:bg-white/10 ${audioSource === 'teaser' ? 'bg-white/20 text-white' : ''}`}
                onClick={() => setAudioSource('teaser')}
                data-testid="audio-source-teaser"
              >Teaser</Button>
            </div>
          )}
        </div>

        <CardContent className="p-5 space-y-4">
          {audioError ? (
            <p className="text-sm text-destructive text-center py-2">Failed to load audio. Please try again later.</p>
          ) : (
            <>
              <div
                ref={progressRef}
                className="relative h-2 bg-muted rounded-full cursor-pointer group"
                onClick={handleSeek}
                data-testid="audio-progress-bar"
              >
                <div className="absolute inset-y-0 left-0 bg-[#1A9DA5] rounded-full transition-all" style={{ width: `${progress}%` }} />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#1A9DA5] rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ left: `calc(${progress}% - 8px)` }}
                />
                {chapters.map((ch, i) => (
                  ch.startTime > 0 && duration > 0 && (
                    <div
                      key={i}
                      className="absolute top-0 w-0.5 h-full bg-foreground/20"
                      style={{ left: `${(ch.startTime / duration) * 100}%` }}
                      title={ch.title}
                    />
                  )
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={restart} data-testid="audio-restart-btn">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => skipChapter(-1)} disabled={activeChapter === 0} data-testid="audio-prev-chapter">
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    className="h-11 w-11 rounded-full bg-[#1A9DA5] hover:bg-[#158D92] text-white shadow-md"
                    onClick={togglePlay}
                    data-testid="audio-play-btn"
                  >
                    {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => skipChapter(1)} disabled={activeChapter >= chapters.length - 1} data-testid="audio-next-chapter">
                    <SkipForward className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground font-mono tabular-nums" data-testid="audio-time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleMute} data-testid="audio-mute-btn">
                      {muted || volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </Button>
                    <input
                      type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1.5 accent-[#1A9DA5] cursor-pointer"
                      data-testid="audio-volume"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {chapters.length > 0 && (
        <Card data-testid="chapter-navigation">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-sm font-semibold font-['Outfit'] flex items-center gap-2">
                <ListMusic className="w-4 h-4 text-[#1A9DA5]" />
                Chapters ({chapters.length})
              </h3>
              <Button
                variant="ghost" size="sm"
                onClick={() => setShowTranscript(!showTranscript)}
                className="text-xs"
                data-testid="toggle-transcript-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-1" />
                {showTranscript ? 'Hide Script' : 'Show Script'}
              </Button>
            </div>
            <ScrollArea className="max-h-[400px]">
              <div className="divide-y">
                {chapters.map((ch, i) => (
                  <div key={i} data-testid={`chapter-item-${i}`}>
                    <button
                      onClick={() => seekToChapter(i)}
                      className={`w-full text-left px-5 py-3 flex items-start gap-3 transition-colors hover:bg-accent/50 ${
                        (useChapterAudio ? chapterAudioIndex : activeChapter) === i ? 'bg-[#1A9DA5]/5 border-l-2 border-l-[#1A9DA5]' : ''
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${
                        (useChapterAudio ? chapterAudioIndex : activeChapter) === i
                          ? 'bg-[#1A9DA5] text-white'
                          : i < (useChapterAudio ? chapterAudioIndex : activeChapter) ? 'bg-[#1A9DA5]/20 text-[#1A9DA5]' : 'bg-muted text-muted-foreground'
                      }`}>
                        {(useChapterAudio ? chapterAudioIndex : activeChapter) === i && playing ? (
                          <div className="flex items-end gap-px h-3">
                            <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '40%', animationDelay: '0ms' }} />
                            <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '70%', animationDelay: '150ms' }} />
                            <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '50%', animationDelay: '300ms' }} />
                          </div>
                        ) : ch.number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-medium truncate ${(useChapterAudio ? chapterAudioIndex : activeChapter) === i ? 'text-[#1A9DA5]' : ''}`}>{ch.title}</p>
                          <Badge variant="outline" className={`text-xs shrink-0 font-mono ${(ch.audioBase64 || ch.audioAssetId) ? '' : 'opacity-50'}`}>
                            {(ch.audioBase64 || ch.audioAssetId) ? <AudioLines className="w-2.5 h-2.5 mr-1" /> : <Clock className="w-2.5 h-2.5 mr-1" />}
                            {(ch.audioBase64 || ch.audioAssetId) ? 'Ready' : formatTime(ch.startTime)}
                          </Badge>
                        </div>
                        {(useChapterAudio ? chapterAudioIndex : activeChapter) === i && (
                          <div className="mt-1.5 h-1 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-[#1A9DA5]/60 rounded-full transition-all duration-300" style={{ width: `${chapterProgress}%` }} />
                          </div>
                        )}
                      </div>
                    </button>
                    {showTranscript && ch.content && (
                      <div className={`px-5 pb-3 pl-16 ${(useChapterAudio ? chapterAudioIndex : activeChapter) === i ? 'bg-[#1A9DA5]/5' : ''}`}>
                        <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-6">
                          {ch.content}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
